// apps/client/src/components/layout/MorphingSidebar.tsx
// Morphing sidebar with two modes: HOVER MODE and LOCKED MODE

import React from 'react';
import { NavLink, useMatch, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCapability } from '../../hooks/useCapability';
import {
  LayoutDashboard, CloudUpload, UsersRound, FileBarChart, Settings, Building2,
  LogOut, Sparkles, Table, SearchCheck, Lock, Users, ChevronLeft, ChevronRight,
  ClipboardCheck, UserCog, Gift, Megaphone, Target, Contact, CalendarClock, Workflow, Send,
} from 'lucide-react';
import { SubscriptionTier } from '../../config/subscriptionTiers';
import { useSubscriptionTier } from '../../hooks/useSubscriptionTier';
import { sidebarReportsLinkTarget } from '../../hooks/reports/resolveReportAccess';
import { usePipelineTrackerAccess } from '../../hooks/pipelineTracker/usePipelineTrackerAccess';
import { canSeePartnerReferrals, partnerNetworkEntitlementCompanyId } from '../../utils/partnerReferrals';
import { useMyReferralProgramAccess } from '../../hooks/useMyReferralProgramAccess';
import { useFeatureEntitlement } from '../../hooks/useFeatureEntitlement';
import { adminPageDenied } from '../../config/adminAccess';

// Design tokens
const COLLAPSED_WIDTH = '4.5rem';
const EXPANDED_WIDTH = '18rem';
const SPRING_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const TRANSITION_DURATION = '300ms';
const COLLAPSE_DELAY = 300;

interface MorphingSidebarProps {
  userData: any;
  companyData: any;
  offices: any[];
  allTemplates: any[];
  // Lifted state from parent
  mode: 'hover' | 'locked';
  isExpanded: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onEnableHoverMode: () => void;
  onPinSidebar: () => void;
  onToggleSidebar: () => void;
}

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
  locked?: boolean;
  tierRequired?: SubscriptionTier;
  isExpanded: boolean;
}

const NavItem: React.FC<NavItemProps> = ({
  to,
  icon,
  label,
  end = false,
  locked = false,
  tierRequired,
  isExpanded,
}) => {
  const tierBadgeColors: Record<SubscriptionTier, string> = {
    plus: 'bg-cyan-500/20 text-cyan-400',
    pro: 'bg-purple-500/20 text-purple-400',
    enterprise: 'bg-amber-500/20 text-amber-400',
    network: 'bg-slate-500/20 text-slate-300',
  };

  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center my-0.5 rounded-lg transition-all duration-200 group
        ${isExpanded ? 'gap-3 px-3 py-2.5' : 'justify-center tooltip-right'}
        ${locked
          ? 'text-gray-500 cursor-not-allowed'
          : isActive
            ? 'bg-gradient-to-r from-blue-600/90 to-blue-500/90 text-white border border-blue-400/50 shadow-lg shadow-blue-600/40'
            : 'text-gray-300 hover:bg-gray-700/50 hover:text-white border border-transparent'
        }`
      }
      {...(!isExpanded && { 'data-tooltip': label })}
    >
      <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
        {icon}
      </span>
      <span
        className="whitespace-nowrap overflow-hidden transition-all"
        style={{
          opacity: isExpanded ? 1 : 0,
          maxWidth: isExpanded ? '12rem' : 0,
          transitionDuration: TRANSITION_DURATION,
          transitionTimingFunction: SPRING_EASING,
        }}
      >
        {label}
      </span>
      {isExpanded && locked && tierRequired && (
        <span className={`ml-auto flex items-center gap-1 px-1.5 py-0.5 text-[0.625rem] font-medium rounded ${tierBadgeColors[tierRequired]}`}>
          <Lock className="w-2.5 h-2.5" />
        </span>
      )}
    </NavLink>
  );
};

const MorphingSidebar: React.FC<MorphingSidebarProps> = ({
  userData,
  companyData,
  offices,
  mode,
  isExpanded,
  onMouseEnter,
  onMouseLeave,
  onEnableHoverMode,
  onPinSidebar,
  onToggleSidebar,
}) => {
  const { logout, hideCommissions, claims } = useAuth();
  const location = useLocation();

  // Broker family helper — broker + broker_assistant + credit_analyst share the same nav restrictions
  const isBrokerFamilyRole = userData?.role === 'broker' || userData?.role === 'broker_assistant' || userData?.role === 'credit_analyst';
  // Marketing operator — sees Lead Capture + Broker Activity (minus Trail Leakage), no commission nav
  const isMarketingRole = userData?.role === 'marketing' || userData?.role === 'service_staff';

  // Reports nav-link target (null = link hidden). Shared resolver reproduces the
  // former inline visibility guard + slug→path selection byte-for-byte.
  const reportsLinkTarget = sidebarReportsLinkTarget(userData?.role, userData?.allowedReports);

  // Capability checks for capability-gated nav items (2026-05-19).
  // legacyAllow arrays MUST mirror the corresponding RequireCapability guard
  // legacyAllow arrays in AppRoutes.tsx so kill-switch-OFF tenants stay
  // byte-identical with the pre-migration RequireRole gates.
  const canUseAiAssistant = useCapability('use_ai_assistant', ['super_admin', 'administrator']);

  // Path matching for active states
  const reportsMatch = useMatch('/reports/*');
  const officesMatch = useMatch('/offices/*');
  const brokersMatch = useMatch('/brokers/*');
  const settingsMatch = useMatch('/settings/*');
  const loanBooksMatch = location.pathname === '/loan-books';

  // Subscription/feature gating. Single source of truth: `useSubscriptionTier`.
  // This component previously carried a LOCAL copy of the precedence rules that
  // silently omitted the per-company DENY (`featureFlags[feature] === false`), so a
  // tenant that had explicitly switched a feature OFF still saw its nav item.
  // The company doc is passed explicitly because the sidebar renders as a SIBLING of
  // AppLayout's `<Outlet>`, so the outlet context the hook normally reads is null here.
  const { hasFeature, getRequiredTier } = useSubscriptionTier(companyData);
  // Referral Partners + Rewards add-on: asked of the entitlement SSOT only when the flag does not decide.
  const { hasFeature: partnerNetworkAddOn } = useFeatureEntitlement(partnerNetworkEntitlementCompanyId(companyData), 'partner_network');
  // Desk-only viewers (e.g. marketing) see Partner Referrals when their company operates a program.
  const myReferralProgram = useMyReferralProgramAccess({ userData, companyData });
  // SalesTrekker Pipeline Tracker: an add-on granted per tenant as an
  // entitlements/config record (never a featureFlags grant), with a separate
  // per-tenant rollout flag. The hook answers from the server entitlement SSOT,
  // and showInNav is true only at rollout 'enabled'; at 'observe' the page is
  // reachable by URL but stays out of the menu.
  const trackerAccess = usePipelineTrackerAccess(userData?.companyId, companyData?.featureFlags);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Silent fail
    }
  };

  // Check if a section is active
  const isReportsActive = !!reportsMatch;
  const isOfficesActive = !!officesMatch || !!brokersMatch;
  const isSettingsActive = !!settingsMatch || loanBooksMatch;

  return (
    <aside
      className="h-screen bg-gray-900 border-r border-gray-800 flex flex-col"
      style={{
        width: isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        transition: `width ${TRANSITION_DURATION} ${SPRING_EASING}`,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Header with Logo, VALUTTEN text, company name, and toggle */}
      <div className={`flex items-center h-[3.5rem] border-b border-gray-800 flex-shrink-0 overflow-visible relative z-50 ${isExpanded ? 'justify-between px-3' : 'justify-center'}`}>
        <div className={`flex items-center min-w-0 ${isExpanded ? 'gap-3' : ''}`}>
          {/* V Logo - Green gradient like VALUTTEN brand */}
          <div className="w-10 h-10 bg-gradient-to-br from-lime-400 to-green-600 rounded-lg flex items-center justify-center text-white font-bold text-base flex-shrink-0 shadow-lg shadow-green-600/30">
            V
          </div>
          {/* VALUTTEN text and company name */}
          <div
            className="overflow-hidden whitespace-nowrap transition-all"
            style={{
              opacity: isExpanded ? 1 : 0,
              maxWidth: isExpanded ? '10rem' : 0,
              transitionDuration: TRANSITION_DURATION,
              transitionTimingFunction: SPRING_EASING,
            }}
          >
            <p className="font-semibold text-white leading-tight">VALUTTEN</p>
            <p className="text-xs text-gray-400 truncate">{companyData?.name || 'Company'}</p>
          </div>
        </div>

        {/* Mode-specific toggle button */}
        {mode === 'hover' && isExpanded && (
          <button
            onClick={onPinSidebar}
            className="tooltip-bottom p-2 rounded-lg transition-all duration-200 flex-shrink-0 text-gray-400 hover:text-white hover:bg-purple-600/20 border border-transparent hover:border-purple-500/50"
            data-tooltip="Pin sidebar open"
          >
            <Lock className="w-5 h-5" />
          </button>
        )}

        {mode === 'locked' && isExpanded && (
          <button
            onClick={onToggleSidebar}
            className="tooltip-bottom p-2 rounded-lg transition-all duration-200 flex-shrink-0 text-white bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/50 hover:border-purple-500/70"
            data-tooltip="Collapse sidebar"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className={`flex-1 py-3 ${isExpanded ? 'px-2 overflow-y-auto overflow-x-hidden' : 'px-4 overflow-visible'}`}>
        {/* Expand button when collapsed in locked mode - matches BF icon size */}
        {mode === 'locked' && !isExpanded && (
          <button
            onClick={onToggleSidebar}
            className="tooltip-right flex items-center justify-center mb-2 rounded-lg text-white bg-gradient-to-r from-green-600/20 to-lime-500/20 hover:from-green-600/30 hover:to-lime-500/30 border border-green-500/50 hover:border-green-500/70 shadow-lg shadow-green-600/20 transition-all duration-200"
            data-tooltip="Expand sidebar"
          >
            <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
              <ChevronRight className="w-6 h-6" />
            </span>
          </button>
        )}

        {/* Main navigation items.
            - Dashboard hidden for broker-family with hideCommissions OR for marketing
              (marketing's default landing is /leads/inbox; allowedReports unlocks reports).
            - Commissions / Upload / Team hidden for broker-family AND for marketing
              (marketing has no commission/upload/team-management responsibilities).
            - Commissions additionally hidden for ANY hidden-commission user: the
              page is entirely commission dollars, so it reads as all em dashes.
              `RequireCommissionAccess` on the route is deliberately UNCHANGED - 
              this hides the affordance, it is not the access control. */}
        {!(isBrokerFamilyRole && hideCommissions) && !isMarketingRole && (
          <NavItem
            to={isBrokerFamilyRole && claims?.officeIds?.[0] ? `/offices/${claims.officeIds[0]}` : '/'}
            icon={<LayoutDashboard className="w-5 h-5" />}
            label="Dashboard"
            end={!isBrokerFamilyRole}
            isExpanded={isExpanded}
          />
        )}

        {!isBrokerFamilyRole && !isMarketingRole && !hideCommissions && !adminPageDenied(userData, 'commissions') && (
          <NavItem
            to="/commission-breakdown"
            icon={<Table className="w-5 h-5" />}
            label="Commissions"
            isExpanded={isExpanded}
          />
        )}

        {!isBrokerFamilyRole && !isMarketingRole && (
          <NavItem
            to="/upload"
            icon={<CloudUpload className="w-5 h-5" />}
            label="Upload"
            isExpanded={isExpanded}
          />
        )}

        {!isBrokerFamilyRole && !isMarketingRole && (
          <NavItem
            to="/team"
            icon={<UsersRound className="w-5 h-5" />}
            label="Team"
            isExpanded={isExpanded}
          />
        )}

        {['administrator', 'super_admin'].includes(userData?.role) && (
          <NavItem
            to="/contact-activity"
            icon={<ClipboardCheck className="w-5 h-5" />}
            label="Contact Activity"
            isExpanded={isExpanded}
          />
        )}

        {/* Broker-family see Client Care only when attached to a broker (brokerDocId).
            An unattached assistant/analyst (extreme edge) is hidden, not shown an
            empty page. Admins/office always; the server hasAccess flag is the
            authoritative page-level backstop. */}
        {['super_admin', 'administrator', 'office_admin', 'broker', 'broker_assistant', 'credit_analyst'].includes(userData?.role) && hasFeature('client_care') && (!isBrokerFamilyRole || !!claims?.brokerDocId) && (
          <NavItem
            to="/clients"
            icon={<Contact className="w-5 h-5" />}
            label="Client Care"
            isExpanded={isExpanded}
          />
        )}

        {(['administrator', 'super_admin'].includes(userData?.role) ||
          (userData?.role === 'office_admin' &&
            companyData?.featureFlags?.office_admin_access_controls === true)) &&
          hasFeature('client_care') && (
          <NavItem
            to="/client-care-settings"
            icon={<CalendarClock className="w-5 h-5" />}
            label="Client Care Settings"
            isExpanded={isExpanded}
          />
        )}

        {/* Partner Referrals — `featureFlags.partner_network` (true grants, false denies) or the
            add-on grant, plus the role allowlist; the /partner-referrals route guard uses the same predicate. */}
        {canSeePartnerReferrals(userData?.role, companyData?.featureFlags, partnerNetworkAddOn, myReferralProgram.hasProgram) && (
          <NavItem
            to="/partner-referrals"
            icon={<Send className="w-5 h-5" />}
            label="Partner Referrals"
            isExpanded={isExpanded}
          />
        )}

        {['administrator', 'super_admin'].includes(userData?.role) && (
          <NavItem
            to="/client-groups"
            icon={<Users className="w-5 h-5" />}
            label="Client Groups"
            isExpanded={isExpanded}
          />
        )}

        {['administrator', 'super_admin', 'office_admin', 'broker', 'broker_assistant', 'credit_analyst', 'marketing', 'service_staff'].includes(userData?.role) && hasFeature('lead_capture') && (
          <NavItem
            to="/leads/inbox"
            icon={<Megaphone className="w-5 h-5" />}
            label="Lead Capture"
            isExpanded={isExpanded}
          />
        )}

        {/* DM Inbox (Meta Messenger Page DMs) is no longer a top-level
            sidebar entry. As of 2026-05-25 it lives inside the Lead Capture
            CRM add-on as a tab in LeadCaptureTopNav (/leads/dm-inbox), so it
            is only visible to tenants entitled to lead_capture. Per-tenant
            message behaviour is still governed by
            featureFlags.meta_messenger_dm.mode at the webhook. */}

        {['administrator', 'super_admin', 'office_admin', 'broker', 'broker_assistant', 'credit_analyst', 'marketing'].includes(userData?.role) && hasFeature('broker_activity') && (
          <NavItem
            to="/broker-activity/pipeline"
            icon={<Target className="w-5 h-5" />}
            label="Team Activity"
            isExpanded={isExpanded}
          />
        )}

        {/* Commission Settings page — gated by per-tenant `featureFlags.commission_settings`
            on the company doc. Activation is a runtime Firestore flip (no code deploy);
            replaces the prior hardcoded company-ID allowlist. */}
        {['administrator', 'super_admin'].includes(userData?.role) && companyData?.featureFlags?.commission_settings === true && !adminPageDenied(userData, 'commission-settings') && (
          <NavItem
            to="/broker-commission-settings"
            icon={<UserCog className="w-5 h-5" />}
            label="Commission Settings"
            isExpanded={isExpanded}
          />
        )}

        {isBrokerFamilyRole && companyData?.featureFlags?.commission_settings === true && (
          !userData?.allowedReports ||
          !Array.isArray(userData.allowedReports) ||
          userData.allowedReports.length === 0 ||
          userData.allowedReports.includes('commission-settings')
        ) && (
          <NavItem
            to="/my-commission-settings"
            icon={<UserCog className="w-5 h-5" />}
            label="My Commission"
            isExpanded={isExpanded}
          />
        )}

        {['administrator', 'super_admin'].includes(userData?.role) && hasFeature('loyalty_program') && (
          <NavItem
            to="/loyalty"
            icon={<Gift className="w-5 h-5" />}
            label="Loyalty Rewards"
            isExpanded={isExpanded}
          />
        )}

        {canUseAiAssistant && (
          <NavItem
            to="/ai-assistant"
            icon={<Sparkles className="w-5 h-5" />}
            label="AI Assistant"
            locked={!hasFeature('ai_assistant')}
            tierRequired={getRequiredTier('ai_assistant')}
            isExpanded={isExpanded}
          />
        )}

        {/* Divider — hidden for marketing: every section between this and the
            Settings divider is hidden for them, which would orphan the line. */}
        {!isMarketingRole && (
          <div className="my-3 mx-2 border-t border-gray-700/50" />
        )}

        {/* Offices — hidden for broker family roles AND marketing */}
        {!isBrokerFamilyRole && !isMarketingRole && (
        <NavLink
          to={offices.length > 0 ? `/offices/${offices[0].id}` : '/offices'}
          className={`flex items-center my-0.5 rounded-lg transition-all duration-200
            ${isExpanded ? 'gap-3 px-3 py-2.5' : 'justify-center tooltip-right'}
            ${isOfficesActive
              ? 'bg-gray-800/60 text-white border-l-2 border-green-500 shadow-lg shadow-green-600/20'
              : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
            }`}
          {...(!isExpanded && { 'data-tooltip': 'Offices' })}
        >
          <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
            <Building2 className="w-5 h-5" />
          </span>
          <span
            className="whitespace-nowrap overflow-hidden transition-all"
            style={{
              opacity: isExpanded ? 1 : 0,
              maxWidth: isExpanded ? '12rem' : 0,
              transitionDuration: TRANSITION_DURATION,
              transitionTimingFunction: SPRING_EASING,
            }}
          >
            Offices
          </span>
        </NavLink>
        )}

        {/* Reports — hidden entirely for marketing users with no allowedReports.
            Broker default to first allowed report; non-broker default to trail-run-off.
            Visibility guard + link target come from the shared resolver
            (sidebarReportsLinkTarget returns null exactly when the Reports link
            should be hidden). commission-settings keeps its own dedicated "My
            Commission" sidebar entry (line ~328); the resolver skips it when
            picking the first report so it never shadows this link's target. */}
        {reportsLinkTarget !== null && (
        <NavLink
          to={reportsLinkTarget}
          className={`flex items-center my-0.5 rounded-lg transition-all duration-200
            ${isExpanded ? 'gap-3 px-3 py-2.5' : 'justify-center tooltip-right'}
            ${isReportsActive
              ? 'bg-gray-800/60 text-white border-l-2 border-green-500 shadow-lg shadow-green-600/20'
              : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
            }`}
          {...(!isExpanded && { 'data-tooltip': 'Reports' })}
        >
          <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
            <FileBarChart className="w-5 h-5" />
          </span>
          <span
            className="whitespace-nowrap overflow-hidden transition-all"
            style={{
              opacity: isExpanded ? 1 : 0,
              maxWidth: isExpanded ? '12rem' : 0,
              transitionDuration: TRANSITION_DURATION,
              transitionTimingFunction: SPRING_EASING,
            }}
          >
            Reports
          </span>
        </NavLink>
        )}

        {/* Acquisition Analysis */}
        {['administrator', 'super_admin'].includes(userData?.role) && (
          <NavItem
            to="/acquisition-analysis"
            icon={<SearchCheck className="w-5 h-5" />}
            label="Acquisition Analysis"
            locked={!hasFeature('acquisition_analysis')}
            tierRequired={getRequiredTier('acquisition_analysis')}
            isExpanded={isExpanded}
          />
        )}

        {/* SalesTrekker Pipeline Tracker (add-on, per-tenant rollout) */}
        {['administrator', 'super_admin'].includes(userData?.role) && trackerAccess.showInNav && (
          <NavItem
            to="/pipeline-tracker"
            icon={<Workflow className="w-5 h-5" />}
            label="SalesTrekker"
            isExpanded={isExpanded}
          />
        )}

        {/* Book Value Report and Book Trend Report have moved into Reports →
            Portfolio. See UnifiedTopBar portfolioItems for gate logic
            (per-tenant flag + role + broker-family allowedReports). */}

        {/* Divider */}
        <div className="my-3 mx-2 border-t border-gray-700/50" />

        {/* Settings: all roles incl. marketing. SettingsPage tab-gates to
            Profile / Security (2FA) / Privacy for everyone; admin tabs render
            only for admins (so marketing can manage name/password/2FA/consent). */}
        <NavLink
          to="/settings"
          className={`flex items-center my-0.5 rounded-lg transition-all duration-200
            ${isExpanded ? 'gap-3 px-3 py-2.5' : 'justify-center tooltip-right'}
            ${isSettingsActive
              ? 'bg-gray-800/60 text-white border-l-2 border-green-500 shadow-lg shadow-green-600/20'
              : 'text-gray-300 hover:bg-gray-700/50 hover:text-white'
            }`}
          {...(!isExpanded && { 'data-tooltip': 'Settings' })}
        >
          <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
            <Settings className="w-5 h-5" />
          </span>
          <span
            className="whitespace-nowrap overflow-hidden transition-all"
            style={{
              opacity: isExpanded ? 1 : 0,
              maxWidth: isExpanded ? '12rem' : 0,
              transitionDuration: TRANSITION_DURATION,
              transitionTimingFunction: SPRING_EASING,
            }}
          >
            Settings
          </span>
        </NavLink>

        {/* LOCKED MODE: Enable hover mode button - below Settings */}
        {mode === 'locked' && isExpanded && (
          <button
            onClick={onEnableHoverMode}
            className="w-full flex items-center gap-3 px-3 py-2.5 mt-2 text-sm rounded-lg transition-all duration-200 text-gray-400 hover:text-white bg-gradient-to-r from-purple-600/10 to-blue-600/10 border border-purple-500/30 hover:border-purple-500/60 hover:from-purple-600/20 hover:to-blue-600/20"
          >
            <Lock className="w-5 h-5" />
            <span>Enable Hover Mode</span>
          </button>
        )}
      </nav>

      {/* Footer: User info and logout */}
      <div className={`py-3 border-t border-gray-800 flex-shrink-0 ${isExpanded ? 'px-3' : 'px-4'}`}>
        {isExpanded && (
          <div className="mb-3 px-2">
            <p className="text-sm font-medium text-white truncate">
              {userData?.displayName || 'User'}
            </p>
            <p className="text-xs text-gray-400 capitalize">
              {userData?.role?.replace('_', ' ') || 'Role'}
            </p>
          </div>
        )}

        <button
          onClick={handleLogout}
          className={`w-full flex items-center rounded-lg text-gray-400 hover:bg-gray-700/50 hover:text-white transition-colors ${isExpanded ? 'gap-3 px-3 py-2' : 'justify-center tooltip-right'}`}
          {...(!isExpanded && { 'data-tooltip': 'Logout' })}
        >
          <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
            <LogOut className="w-5 h-5" />
          </span>
          <span
            className="whitespace-nowrap overflow-hidden transition-all"
            style={{
              opacity: isExpanded ? 1 : 0,
              maxWidth: isExpanded ? '10rem' : 0,
              transitionDuration: TRANSITION_DURATION,
              transitionTimingFunction: SPRING_EASING,
            }}
          >
            Logout
          </span>
        </button>
      </div>
    </aside>
  );
};

export default MorphingSidebar;
